import {
  PUBLIC_RUNTIME_KEYS,
  RUNTIME_CONFIG_GLOBAL,
  type PublicRuntimeKey,
  type RuntimeConfig,
} from './runtimeKeys';

/**
 * Runtime config read from `/config.js`, rendered by the container at start-up.
 * No `import.meta.env` fallback: a computed key leaks every VITE_ var into the bundle.
 */

declare global {
  interface Window {
    [RUNTIME_CONFIG_GLOBAL]?: RuntimeConfig;
  }
}

/** Centre of the service area, used until a deployment configures its own. */
const DEFAULT_MAP_CENTER: [lon: number, lat: number] = [
  -38.95954656962549, -12.253556253456116,
];

/** Keys that resolved to nothing, reported as one group by `reportRuntimeConfig`. */
const missing: string[] = [];
/** Keys that were present but malformed, reported alongside the missing ones. */
const invalid: string[] = [];

function readRaw(key: PublicRuntimeKey): string {
  const runtime =
    typeof window === 'undefined'
      ? undefined
      : window[RUNTIME_CONFIG_GLOBAL]?.[key];
  if (typeof runtime === 'string' && runtime !== '') return runtime;

  missing.push(key);
  return '';
}

/** Reads an http(s) URL; invalid values would otherwise reach the CPE via TR-069. */
function readUrl(key: PublicRuntimeKey): string {
  const raw = readRaw(key);
  if (!raw) return '';

  try {
    const { protocol } = new URL(raw);
    if (protocol !== 'http:' && protocol !== 'https:') {
      invalid.push(`${key} (unsupported protocol "${protocol}")`);
      return '';
    }
    return raw;
  } catch {
    invalid.push(`${key} (not a valid URL)`);
    return '';
  }
}

function readCoordinate(key: PublicRuntimeKey, limit: number): number | null {
  const raw = readRaw(key);
  if (!raw) return null;

  const value = Number(raw);
  if (!Number.isFinite(value) || Math.abs(value) > limit) {
    invalid.push(`${key} (expected a number within +/-${limit}, got "${raw}")`);
    return null;
  }
  return value;
}

function readMapCenter(): [lon: number, lat: number] {
  const lon = readCoordinate('VITE_MAP_DEFAULT_CENTER_LON', 180);
  const lat = readCoordinate('VITE_MAP_DEFAULT_CENTER_LAT', 90);

  // Both or neither, so a half-configured centre falls back to the default.
  if (lon === null || lat === null) return [...DEFAULT_MAP_CENTER];
  return [lon, lat];
}

export const env = Object.freeze({
  /** Mapbox public (`pk.`) token. Restrict it by URL in the Mapbox account. */
  mapboxToken: readRaw('VITE_MAPBOX_TOKEN'),

  /** Map centre used when nothing else determines the viewport. */
  mapDefaultCenter: readMapCenter(),

  /** Speed-test endpoints, keyed by the server selector shown in the UI. */
  speedtestServers: {
    '1': {
      download: readUrl('VITE_SPEEDTEST_DOWNLOAD_URL'),
      upload: readUrl('VITE_SPEEDTEST_UPLOAD_URL'),
    },
    '2': {
      download: readUrl('VITE_SPEEDTEST_DOWNLOAD_URL_2'),
      upload: readUrl('VITE_SPEEDTEST_UPLOAD_URL_2'),
    },
  },
});

/** Logs one grouped warning for missing or invalid keys. */
export function reportRuntimeConfig(): void {
  if (missing.length === 0 && invalid.length === 0) return;

  const source =
    typeof window !== 'undefined' && window[RUNTIME_CONFIG_GLOBAL]
      ? '/config.js'
      : '/config.js (absent -- the app is running on built-in defaults)';

  console.warn(
    [
      `Runtime configuration incomplete (source: ${source}).`,
      missing.length ? `Missing: ${missing.join(', ')}.` : '',
      invalid.length ? `Invalid: ${invalid.join('; ')}.` : '',
      `Expected keys: ${PUBLIC_RUNTIME_KEYS.join(', ')}.`,
      'Set these in the deployment environment (docker-compose.yml) or in .env for local development.',
    ]
      .filter(Boolean)
      .join('\n')
  );
}
